import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import UpdateAvailableBanner from './UpdateAvailableBanner';
import StagingBanner from './StagingBanner';
import PullToRefresh from './PullToRefresh';
import ErrorBoundary from './ErrorBoundary';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Lazy — the routing check below picks exactly ONE of these (or App) before anything mounts, so
// eagerly importing all 5 forced every admin/technician login to download ~2,400 lines of code
// (the customer portal app, 3 public single-document viewers, the QuickBooks OAuth callback) it
// would never use that session. App itself stays a plain import above since it's the overwhelming
// majority path — no fallback UI would ever show for it anyway (Suspense only suspends on a
// lazy child actually being rendered, never on a plain synchronous one).
const PublicEstimateView = lazy(() => import('./PublicEstimateView'));
const PublicInvoiceView = lazy(() => import('./PublicInvoiceView'));
const CustomerPortalApp = lazy(() => import('./CustomerPortalApp'));
const QBOAuthCallback = lazy(() => import('./QBOAuthCallback'));

// This app has no router at all — every other screen is a conditional render inside App.js
// based on auth state. Adding a full routing library for these separate top-level trees would
// be a much bigger change than any of them individually needs, so /estimate/:id, /invoice/:id,
// /change-order/:id, /portal, and /quickbooks/callback are recognized here, before App (and its
// hooks) ever mounts. The first three render a completely separate, auth-free single-document
// view; /portal renders a whole separate authenticated tree (CustomerPortalApp) for logged-in
// customers; /quickbooks/callback is the Intuit OAuth redirect target — still relies on the
// admin's existing Firebase Auth session (persisted across the redirect), just doesn't need
// App.js's full company/feature-flag context to finish the handshake.
const estimatePathMatch = window.location.pathname.match(/^\/estimate\/([^/]+)/);
const invoicePathMatch = window.location.pathname.match(/^\/invoice\/([^/]+)/);
const isPortalPath = window.location.pathname.match(/^\/portal(\/|$)/);
const isQBOCallbackPath = window.location.pathname.match(/^\/quickbooks\/callback(\/|$)/);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <StagingBanner />
      <UpdateAvailableBanner />
      <PullToRefresh>
        {/* Inline styles, not App.css's .loading-screen class — that class is only ever loaded
            once App.js's own bundle is, which isn't guaranteed yet for the 4 non-App lazy routes
            below (e.g. a first-ever visit to /portal never loads App.js's chunk at all). */}
        <Suspense fallback={
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', color: '#5B6672' }}>
            Loading...
          </div>
        }>
          {estimatePathMatch ? <PublicEstimateView estimateId={estimatePathMatch[1]} />
            : invoicePathMatch ? <PublicInvoiceView invoiceId={invoicePathMatch[1]} />
            : isPortalPath ? <CustomerPortalApp />
            : isQBOCallbackPath ? <QBOAuthCallback />
            : <App />}
        </Suspense>
      </PullToRefresh>
    </ErrorBoundary>
  </React.StrictMode>
);

// Registers the service worker in production builds only, giving the app offline app-shell
// caching and installability. Deliberately not used in dev (npm start) so local changes are
// always reflected immediately without a stale cached bundle. onUpdate fires once a new deploy's
// service worker has finished installing in the background and is sitting WAITING for every tab
// to close (the default CRA behavior) — bridged to UpdateAvailableBanner via a plain window
// event since this runs outside the React tree.
serviceWorkerRegistration.register({
  onUpdate: (registration) => {
    window.dispatchEvent(new CustomEvent('sw-update-available', { detail: registration }));
  }
});